import { PermissionEnum } from 'api/userPermissions'
import { AuthGuard } from 'components/auth'
import { useIsTippingEnabled } from 'hooks/useIsTippingEnabled'
import {
  isShiftMessagable,
  NUM_HOURS_MESSAGING_ALLOWED_AFTER,
  NUM_HOURS_MESSAGING_ALLOWED_BEFORE,
} from 'lib/shift'
import { useUserProfile } from 'queries/user'
import { useTipHistory } from 'queries/worker'
import { useState } from 'react'
import { Box, Button, Flex, Heading, Pluralize, Tooltip } from 'ui'
import { ShiftDetailAssignedWorkers } from './assigned_workers/ShiftDetailAssignedWorkers'
import { BulkTipModal } from './components/BulkTipModal'
import { MessageShiftWorkersModal } from './MessageShiftWorkersModal'
import { ShiftDetailWorkersAlerts } from './ShiftDetailWorkersAlerts'
import { useShiftDetail } from './useShiftDetail'

type BulkTipWorkersButtonProps = {
  shiftId: number
}

const BulkTipWorkersButton = ({ shiftId }: BulkTipWorkersButtonProps) => {
  const isTippingEnabled = useIsTippingEnabled()
  const { data: tipHistory, isLoading } = useTipHistory(
    { shiftId },
    {
      enabled: Boolean(isTippingEnabled),
    }
  )

  const [showBulkTipWorkersModal, setShowBulkTipWorkersModal] = useState(false)

  if (!isTippingEnabled || !tipHistory?.workHistory?.length) return null

  return (
    <AuthGuard.Permission value={PermissionEnum.PaymentAccess}>
      <Button
        kind="medium"
        variant="secondary"
        onClick={() => {
          setShowBulkTipWorkersModal(true)
        }}
        loading={isLoading}
      >
        Tip Workers
      </Button>

      <BulkTipModal
        key={`bulk-tip-modal-${showBulkTipWorkersModal}`}
        isOpen={showBulkTipWorkersModal}
        shiftId={shiftId}
        onClose={() => setShowBulkTipWorkersModal(false)}
      />
    </AuthGuard.Permission>
  )
}

const MessageWorkersButton = () => {
  const { shift } = useShiftDetail()
  const { data: user } = useUserProfile()

  const canMessageWorkers: boolean =
    user?.isInternal || Boolean(isShiftMessagable(shift) && shift?.work?.length)

  const messageWorkersButton = (
    <Button kind="medium" variant="secondary" disabled={!canMessageWorkers}>
      Message Workers
    </Button>
  )

  return (
    <AuthGuard.Permission value={PermissionEnum.MessageWorker}>
      <MessageShiftWorkersModal
        trigger={
          canMessageWorkers ? (
            messageWorkersButton
          ) : (
            <Tooltip
              variant="info"
              style={{ maxWidth: '300px' }}
              content={`Messaging is currently unavailable. You can only message workers on a shift between ${NUM_HOURS_MESSAGING_ALLOWED_BEFORE} hours before the shift starts and up to ${NUM_HOURS_MESSAGING_ALLOWED_AFTER} hours after the shift has ended.`}
            >
              {messageWorkersButton}
            </Tooltip>
          )
        }
      />
    </AuthGuard.Permission>
  )
}

export const ShiftDetailWorkers = () => {
  const { shift, isOtherCompany } = useShiftDetail()

  if (!shift) return null

  const numWorkersAssigned = shift.work?.length || 0

  return (
    <Box>
      <Flex
        flexDirection={['column', 'column', 'row']}
        alignItems={['flex-start', 'flex-start', 'center']}
        flexWrap={'wrap'}
        justifyContent={'space-between'}
        mb={3}
      >
        <Heading level={2} fontWeight={2}>
          Filled{' '}
          {shift?.workersNeeded && numWorkersAssigned > shift.workersNeeded
            ? shift?.workersNeeded
            : numWorkersAssigned}{' '}
          of{' '}
          <Pluralize
            count={shift.workersNeeded || 0}
            oneText={'worker'}
            manyText={'workers'}
          />
        </Heading>
        {isOtherCompany ? null : (
          <Flex gap={2}>
            <BulkTipWorkersButton shiftId={shift.id} />
            <MessageWorkersButton />
          </Flex>
        )}
      </Flex>
      <ShiftDetailWorkersAlerts />
      <ShiftDetailAssignedWorkers />
    </Box>
  )
}
